import React from "react";
import { Shield } from "lucide-react";
import { TabsContent } from "@/components/ui/tabs";
import { useTableControls, SortableTh, SearchBar, applyControls } from "../tableTools";

const SEARCH_FIELDS = [
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "email", label: "Email" },
];

const firstNameOf = (a) => a.first_name || (a.name || "").split(" ")[0] || "";
const lastNameOf = (a) => a.last_name || (a.name || "").split(" ").slice(1).join(" ") || "";

const getValue = (a, field) => {
  switch (field) {
    case "first_name": return firstNameOf(a);
    case "last_name": return lastNameOf(a);
    case "email": return a.email;
    case "source": return a.source;
    default: return "";
  }
};

export const AdministratorsTab = (props) => {
  const { administrators, currentAdminEmail, handleToggleAdminRights } = props;
  const ctrl = useTableControls("first_name", "first_name");
  const rows = applyControls(administrators, ctrl, getValue);
  return (
          <TabsContent value="administrators" data-testid="administrators-content">
            <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
              <div className="p-6 border-b border-zinc-800 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-6 h-6 text-crew-yellow" />
                    <h2 className="text-2xl font-bold text-crew-yellow">ADMINISTRATORS</h2>
                  </div>
                  <p className="text-zinc-400 text-sm">Manage admin access • {administrators.length} total administrators</p>
                </div>
                <SearchBar fields={SEARCH_FIELDS} testid="admin-search" {...ctrl} />
              </div>

              {rows.length === 0 ? (
                <div className="p-12 text-center text-zinc-400" data-testid="no-administrators">
                  <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{administrators.length === 0 ? "No administrators configured." : "No matching administrators."}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="text-xs uppercase text-zinc-400 border-b border-zinc-800 bg-zinc-900">
                      <tr>
                        <SortableTh field="first_name" label="First Name" {...ctrl} onSort={ctrl.handleSort} />
                        <SortableTh field="last_name" label="Last Name" {...ctrl} onSort={ctrl.handleSort} />
                        <SortableTh field="email" label="Email" {...ctrl} onSort={ctrl.handleSort} />
                        <th className="py-3 px-4">Phone</th>
                        <SortableTh field="source" label="Source" {...ctrl} onSort={ctrl.handleSort} />
                        <th className="py-3 px-4 text-center">Admin Access</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((admin, idx) => {
                        const firstName = firstNameOf(admin) || '—';
                        const lastName = lastNameOf(admin) || '—';
                        const isProtected = admin.is_original;
                        const canRevoke = admin.is_participant && admin.id && !isProtected && admin.email?.toLowerCase() !== currentAdminEmail;
                        
                        return (
                          <tr 
                            key={admin.email || idx} 
                            className={`border-b border-zinc-800/50 hover:bg-zinc-900 ${isProtected ? 'bg-crew-yellow/5' : ''}`}
                            data-testid={`admin-row-${idx}`}
                          >
                            <td className="py-3 px-4">
                              <div className="font-bold text-white flex items-center gap-2">
                                {firstName}
                                {isProtected && (
                                  <span className="text-xs bg-zinc-700 text-zinc-300 px-1.5 py-0.5 rounded font-bold">PROTECTED</span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-white font-bold">{lastName}</td>
                            <td className="py-3 px-4 text-zinc-300">{admin.email}</td>
                            <td className="py-3 px-4 text-zinc-300 font-mono">{admin.phone || '—'}</td>
                            <td className="py-3 px-4 text-zinc-400 text-xs">
                              {isProtected ? (
                                <span className="text-crew-yellow font-bold">Original Admin</span>
                              ) : admin.source === 'promoted_from_crew' ? (
                                <span className="text-green-400">Crew Member</span>
                              ) : (
                                <span>Admin Account</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {canRevoke ? (
                                <input
                                  type="checkbox"
                                  checked={true}
                                  onChange={() => handleToggleAdminRights(admin.id, admin.email, false)}
                                  className="w-5 h-5 cursor-pointer accent-crew-yellow"
                                  title="Revoke admin rights"
                                  data-testid={`revoke-admin-${idx}`}
                                />
                              ) : (
                                <span className="text-crew-yellow font-bold text-xs">
                                  {isProtected ? 'PERMANENT' : admin.email?.toLowerCase() === currentAdminEmail ? 'YOU' : '✓'}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
                <p className="text-zinc-500 text-sm">
                  <strong className="text-zinc-400">Note:</strong> To grant admin access to a crew member, go to the "Crew Approvals" tab and check the "Admin" checkbox next to their name.
                  Protected administrators cannot have their access revoked.
                </p>
              </div>
            </div>
          </TabsContent>
  );
};
